import { AuthorizationService } from '../services/authorization/authorization.service';
import { AuthorizationStateService } from '../services/authorization/authorization-state.service';
import { ProfileOrganizationScopeService } from '../services/authorization/profile-organization-scope.service';
import { UserSessionService } from '../services/user-session.service';
import { PermissionDirectiveOptions } from '../models/ui/permission-directive-options.model';
import { AuthorizationModel } from '../models/data/authorization.model';
import { DataEntityTypes } from '../enums/data.enums';
import { AuditPermission, Permission } from '../enums/permissions.enums';
import { AuditEntityType } from '../enums/base.enums';

export class PermissionUtilities {

	static getIsEntityOwner(
		options: PermissionDirectiveOptions,
		userSessionService: UserSessionService,
		authorizationStateService: AuthorizationStateService
	): boolean {
		if (!options.dataEntityType || !options.dataEntityId) return false;

		switch (options.dataEntityType) {
			case DataEntityTypes.User:
				return userSessionService.activeUser()?.userId === options.dataEntityId;
			case DataEntityTypes.Member:
				const authState = authorizationStateService.authorizationState();
				const memberIds = authState?.memberPermissions?.map(mp => mp.memberId);
				return memberIds?.includes(options.dataEntityId) || false;
			default:
				return false;
		}
	}

	// True when the current user holds the permission at the USER (platform) level — it's in their
	// userPermissions, ignoring org-level memberPermissions. Lets staff act on their own account.
	// organizationId is omitted on purpose: hasPermission returns before consulting it when userPermissionOnly is true.
	static hasUserLevelPermission(
		permission: Permission,
		authorizationService: AuthorizationService,
		authorizationStateService: AuthorizationStateService
	): boolean {
		const authState = authorizationStateService.authorizationState();
		return !!authState && authorizationService.hasPermission(authState, permission, undefined, true);
	}

	// The organization to evaluate org-level permissions against, in precedence order: an explicitly
	// passed id, then the organization of the profile page we're rendering inside, then the active
	// organization. isPending is true when a profile scope exists but hasn't resolved its organization
	// yet; callers must NOT fall back to the active organization in that window, because on the exact
	// path this exists for — an Admin of org A viewing org B — the active organization is the more
	// permissive wrong answer (DEV-2643).
	static resolveOrganizationScope(
		explicitOrganizationId: number | undefined,
		profileScope: ProfileOrganizationScopeService | null,
		userSessionService: UserSessionService
	): { organizationId: number | undefined; isPending: boolean } {
		if (explicitOrganizationId !== undefined) {
			return { organizationId: explicitOrganizationId, isPending: false };
		}

		if (profileScope) {
			return { organizationId: profileScope.organizationId(), isPending: profileScope.isPending() };
		}

		return { organizationId: userSessionService.activeOrganization()?.organizationId, isPending: false };
	}

	// A single org-scoped permission check for call sites that resolve their own organization — list
	// filters, hand-rolled row-action gates, audit-log details — rather than going through the
	// permission directives. Mirrors shouldOptionsRender's handling of a pending profile scope: the
	// organization matters but isn't known yet, so answer from the user/platform level only. Staff keep
	// their access with no flicker, and hasPermission is never handed an undefined organizationId,
	// which it treats as "a grant in ANY organization the viewer belongs to" — the exact leak the
	// profile scope exists to close (DEV-2643).
	static hasScopedOrganizationPermission(
		permission: Permission,
		authState: AuthorizationModel,
		authorizationService: AuthorizationService,
		profileScope: ProfileOrganizationScopeService | null,
		userSessionService: UserSessionService
	): boolean {
		const scope = PermissionUtilities.resolveOrganizationScope(undefined, profileScope, userSessionService);

		if (scope.isPending) {
			return authorizationService.hasPermission(authState, permission, undefined, true);
		}

		return authorizationService.hasPermission(authState, permission, scope.organizationId);
	}

	static shouldOptionsRender(
		options: PermissionDirectiveOptions,
		authorizationService: AuthorizationService,
		authorizationStateService: AuthorizationStateService,
		userSessionService: UserSessionService,
		profileScope?: ProfileOrganizationScopeService | null
	): boolean {
		if (!options.requiredPermissions?.length) return true;

		const authState = authorizationStateService.authorizationState();
		if (!authState) return false;

		if (PermissionUtilities.getIsEntityOwner(options, userSessionService, authorizationStateService)) return true;

		// userPermissionOnly checks never consult an organization, so they are answered before the
		// scope is resolved — otherwise a profile page's pending scope would briefly hide staff-only
		// rows (the slug and pricing tier on the organization profile) that have nothing to do with
		// which organization is on screen.
		if (options.userPermissionOnly) {
			return PermissionUtilities.hasAnyUserLevelPermission(options.requiredPermissions, authState, authorizationService);
		}

		// Callers that render an organization other than the active one (e.g. the organization profile
		// page, reachable for any org the viewer belongs to) pass organizationId so org-level
		// permissions are evaluated against the organization on screen rather than the active one.
		// Inside a profile page the page's own ProfileOrganizationScopeService supplies it instead,
		// which is what keeps the checks in reusable child components (property-charge-list,
		// member-list, payment-history-list, ...) pointed at the right organization with no change at
		// those call sites.
		const scope = PermissionUtilities.resolveOrganizationScope(options.organizationId, profileScope ?? null, userSessionService);

		// The organization matters here but isn't known yet. Answer from the user/platform level only:
		// staff keep their access with no flicker, and no org-level grant can leak in from the active
		// organization while the entity fetch is in flight.
		if (scope.isPending) {
			return PermissionUtilities.hasAnyUserLevelPermission(options.requiredPermissions, authState, authorizationService);
		}

		return options.requiredPermissions.some(permission =>
			authorizationService.hasPermission(authState, permission, scope.organizationId));
	}

	// Shared by the two early-outs above: the user/platform-level half of the answer, which is
	// knowable without an organization.
	private static hasAnyUserLevelPermission(
		permissions: Permission[],
		authState: AuthorizationModel,
		authorizationService: AuthorizationService
	): boolean {
		return permissions.some(permission => authorizationService.hasPermission(authState, permission, undefined, true));
	}

	// User audit logs aren't organization-scoped — a user doesn't inherently belong to an organization,
	// so the API evaluates audits.read_details at the USER (platform) level for that route. An org
	// admin's org-scoped grant doesn't apply there, and requesting includeDetails on the strength of
	// it fails the whole request, leaving the Activity tab empty (DEV-2612). Every other audit entity
	// is organization-scoped, so it keeps the organization-scoped check.
	static canViewAuditLogDetails(
		authorizationService: AuthorizationService,
		authorizationStateService: AuthorizationStateService,
		userSessionService: UserSessionService,
		entityType?: AuditEntityType,
		organizationScope?: { organizationId: number | undefined; isPending: boolean }
	): boolean {
		const authState = authorizationStateService.authorizationState();
		if (!authState) return false;

		if (entityType === AuditEntityType.User) {
			return PermissionUtilities.hasUserLevelPermission(AuditPermission.ReadDetails, authorizationService, authorizationStateService);
		}

		// Audit logs for org-scoped entities render on profile pages reachable for any organization the
		// viewer belongs to, so callers there pass the scope resolved from the entity's own organization
		// rather than letting this fall back to the active one (DEV-2643).
		const scope = organizationScope
			?? { organizationId: userSessionService.activeOrganization()?.organizationId, isPending: false };

		// The organization matters here but isn't known yet. Answer from the user/platform level only, so
		// the active organization — the more permissive wrong answer on the exact path the profile scope
		// exists for — never stands in for the one still being fetched. Requesting includeDetails on the
		// strength of a grant elsewhere would fail the whole audit request anyway (DEV-2612).
		if (scope.isPending) {
			return PermissionUtilities.hasUserLevelPermission(AuditPermission.ReadDetails, authorizationService, authorizationStateService);
		}

		return authorizationService.hasPermission(authState, AuditPermission.ReadDetails, scope.organizationId);
	}

	// The viewer's effective minimum authority level for the organization — the stronger (numerically
	// lower) of their user-level and organization-level authority. Mirrors
	// AuthorizationService.GetActingUserMinimumAuthorityLevelAsync (Membershine.API.Core).
	private static getActingAuthorityLevel(
		authState: AuthorizationModel,
		organizationId: number | undefined
	): number | undefined {
		const userLevel = authState.userPermissions?.authorityLevelRequired ?? undefined;
		const orgLevel = authState.memberPermissions?.find(mp => mp.organizationId === organizationId)?.authorityLevelRequired ?? undefined;

		if (userLevel === undefined) return orgLevel;
		if (orgLevel === undefined) return userLevel;
		return Math.min(userLevel, orgLevel);
	}

	// True when the viewer is NOT authorized to modify a member holding the given authority level.
	// Mirrors AuthorizationService.IsAuthorizedToModifyMemberAsync (Membershine.API.Core): the viewer
	// is authorized only when their own effective authority level is numerically lower (more
	// authoritative) than the target's. Comparing raw authority levels — rather than specific role
	// names — means a new custom role is protected correctly with no frontend changes (DEV-2541).
	// organizationId must be the TARGET MEMBER's own organizationId, not the viewer's currently
	// active organization — the backend keys this same check off member.OrganizationId (every
	// IsAuthorizedToModifyMemberAsync call site passes the target member's own org), and the two can
	// diverge on the frontend (e.g. a Staff viewer browsing an org they don't belong to).
	static isProtectedFromMemberModification(
		targetAuthorityLevelRequired: number | undefined | null,
		organizationId: number,
		authorizationStateService: AuthorizationStateService
	): boolean {
		// Target has no authority level requirement, so can always be modified. The API returns this
		// as explicit JSON null (not an omitted field) once a member holds no roles — e.g. immediately
		// after deactivation, which clears MembersToRole — so both null and undefined must short-circuit here.
		if (targetAuthorityLevelRequired === undefined || targetAuthorityLevelRequired === null) return false;

		const authState = authorizationStateService.authorizationState();
		if (!authState) return false;

		const actingAuthorityLevel = PermissionUtilities.getActingAuthorityLevel(authState, organizationId);

		return !(actingAuthorityLevel !== undefined && actingAuthorityLevel < targetAuthorityLevelRequired);
	}

	// Fetches a target member's authority level for the given organization via their own
	// authorization context — the same source AuthorizationService.IsAuthorizedToModifyMemberAsync
	// reads server-side for members with a linked user. Callers should only invoke this on demand
	// (e.g. when a row's Actions dropdown is opened), never eagerly for a whole list, to avoid a
	// request per row (DEV-2541). Members without a linked user (invited but not yet accepted)
	// can't be looked up this way and resolve to undefined — the backend's own authority check still
	// rejects any actual attempt to modify them regardless.
	static async fetchTargetMemberAuthorityLevel(
		targetUserId: number | undefined | null,
		organizationId: number | undefined,
		authorizationService: AuthorizationService
	): Promise<number | undefined> {
		if (!targetUserId) return undefined;

		const result = await authorizationService.getUserRolesAndPermissions(targetUserId);
		if (!result.success || !result.data) return undefined;

		return result.data.memberPermissions?.find(mp => mp.organizationId === organizationId)?.authorityLevelRequired ?? undefined;
	}

	// True when the viewer is NOT authorized to modify a USER holding the given authority level.
	// Mirrors AuthorizationService.IsAuthorizedToModifyAsync(actingUserId, targetUserId) in
	// Membershine.API.Authentication — unlike the member-level check, this ignores org-scoped
	// authority entirely; only the viewer's own user-level authority (System Administrator/Staff/
	// UserReadOnly) counts, since UsersToRole is a separate table from the org-scoped MembersToRole.
	// Callers MUST bypass this for self-actions (matching the backend's own `actingUserId !=
	// targetUserId` guard) — a user's authority level compared against itself is always a tie, which
	// this correctly (but unhelpfully, for self) treats as unauthorized.
	static isProtectedFromUserModification(
		targetAuthorityLevelRequired: number | undefined | null,
		authorizationStateService: AuthorizationStateService
	): boolean {
		if (targetAuthorityLevelRequired === undefined || targetAuthorityLevelRequired === null) return false;

		const actingAuthorityLevel = authorizationStateService.authorizationState()?.userPermissions?.authorityLevelRequired ?? undefined;

		return !(actingAuthorityLevel !== undefined && actingAuthorityLevel < targetAuthorityLevelRequired);
	}

	// Fetches a target user's own user-level authority level — the same source
	// AuthorizationService.IsAuthorizedToModifyAsync reads server-side for the target. Callers should
	// only invoke this on demand (e.g. when a row's Actions dropdown is opened), never eagerly for a
	// whole list, to avoid a request per row (DEV-2541).
	static async fetchTargetUserAuthorityLevel(
		targetUserId: number | undefined | null,
		authorizationService: AuthorizationService
	): Promise<number | undefined> {
		if (!targetUserId) return undefined;

		const result = await authorizationService.getUserRolesAndPermissions(targetUserId);
		if (!result.success || !result.data) return undefined;

		return result.data.userPermissions?.authorityLevelRequired ?? undefined;
	}
}
